/**
 * @module lib/commercial/keytar
 * @description in order for this module to run properly, keytar's init function
 * must be called after the datalayerReady Promise is resolved.
 */
import isFunction from "lodash/isFunction";
import isEmpty from "lodash/isEmpty";
import tgamConfig from "config/tgam";
import { getProp, isTimedDisabled } from "utils/utils";
import Customer from "./Customer";
import { BLOCKOUT_TYPES } from "./Template";
import newslettersWall from "./newsletters";
import newsletterStoryRegwall from "./newsletter-story-regwall";
import housePriceWall from "./house-price-data-centre";
import cannabisSponsoredWall from "./cannabis-sponsored-content";
import globeAdvisorWall from "./globe-advisor";
import subscriberWall from "./subscriber";
import registeredWall from "./registered";
import anonymousWall from "./anonymous";
import promotionWall from "./promotion";
import crosswordsWall from "./crosswords";
import flashSalePaywallHard from "./flash-sale-paywall-hard";
// import flashSalePaywallMetered from "./flash-sale-paywall-metered";
import { mark, measure } from "lib/performance/user-timing";
import logger from "utils/logger";
import keytarDone from "./keytar-done";

const debugTag = "[KEYTAR]";

/**
 * Determines if the current content should always be protected
 * @param {String} paywallStatus - the article content colour
 * @param {String} [url=window.location.href] - optional url to inspect
 * @returns {Boolean} true if the content should be protected
 */
function contentShouldAlwaysBeProtected(paywallStatus, url = window.location.href) {
  if (url.includes("globe-advisor")) {
    return !url.includes("advisor-news");
  }
  return ["red", "yellow"].includes(paywallStatus);
}

/**
 * Don't render Keytar on these page types
 * @type {Array<String>}
 */
const DISALLOWED_PAGETYPES = ["author", "error"];

/**
 * Determine if this rule is currently active
 * @param {Object} c - collection of params
 * @param {Object} c.rule - that contains a `matcher()` method
 * @param {Object} c.customer - of user
 * @param {String} c.paywallStatus - of current article page paywall colour
 * @param {String} c.url - of current page url
 * @param {String} c.tgam - of wall's tgam config value
 * @returns {Boolean} true if rule is active
 */
function ruleIsActive({ rule, customer, paywallStatus, url, tgam }) {
  let result = false;
  if (rule && isFunction(rule.matcher)) {
    result = rule.matcher({ paywallStatus, customer, url, tgam }) === true;
    logger.info(`${debugTag} rule - ${rule.name}:`, result);
  }
  return result;
}

/**
 * Process all the rules associated with a wall
 * @param {Object} c - collection of params
 * @param {Object} c.wall - An array of rule objects
 * @param {Object} c.customer - of user
 * @param {String} c.paywallStatus - of current article page paywall colour
 * @returns {Boolean} true if wall has an active rule
 */
function wallIsActive({ wall, customer, paywallStatus }) {
  let result = false;
  if (wall && wall.enabled && wall.rules && !wall.suppressed()) {
    const url = window.location.toString();
    const activeRule = wall.rules.find(rule => {
      return ruleIsActive({ rule, customer, paywallStatus, url, tgam: wall.config });
    });
    if (activeRule) {
      wall.matchedRule = activeRule;
      wall.customer = customer;
      // Need to reset the parent context, since we set above wall values
      if (wall.template) {
        wall.template.parentContext = wall;
      }
      result = (wall.enabled === true);
    }
  }
  logger.info(`${debugTag} wall - ${wall.name}:`, result);
  return result;
}

/**
 * Finds the first instance in an array of Wall instances where one of the rules match or none
 * @param {Object} c - collection of params
 * @param {Array} [c.walls=[]] An array of Wall class instance
 * @param {Object} [c.profile={}] - user data profile
 * @param {String} [c.paywallStatus=""] - of current article page paywall colour
 * @returns {Object|undefined}
 */
function getKeytarWall({ walls = [], profile = {}, paywallStatus = "" }) {
  const customer = new Customer(profile);
  logger.info(`${debugTag} customer.identity:`, customer.identity);
  logger.info(`${debugTag} paywallStatus: ${paywallStatus}`);
  return walls.find(wall => wallIsActive({ wall, customer, paywallStatus }));
}

/**
 * Get the wall based on Hermes' wall type
 * @param {Object} c - collection of params
 * @param {Array} [c.walls=[]] An array of Wall class instance
 * @param {String} [c.wallType=""] - the wall type (possible values: "nowall", "redwall", or "regwall")
 * @param {Object} [c.profile={}] - user data profile
 * @returns {Object|undefined}
 */
function getHermesWall({ walls, wallType = "", profile = {} }) {
  const wall = walls.find(wall => wall.name === wallType && wall.enabled && !wall.suppressed());

  if (!wall) { // wallType = "nowall"
    return null;
  }
  const customer = new Customer(profile);
  logger.info(`${debugTag} customer.identity:`, customer.identity);

  wall.customer = customer;
  logger.info(`${debugTag} Hermes wall - ${wall.name}: true`);
  return wall;
}

/**
 * Determine if Keytar should be run for the given environment context
 * @param {Object} [config=tgamConfig] - tgam
 * @param {String} [url=window.location.href] - to test if is an admin url
 * @param {Array<String>} [disallowed=DISALLOWED_PAGETYPES] - page types
 * @returns {Boolean}
 */
function shouldRunKeytar(config = tgamConfig, url = window.location.href, disallowed = DISALLOWED_PAGETYPES) {
  const advContent = getProp(config, "meta.advContent", false);
  const isAdvContent = advContent === "true" || advContent === true; // some use String, most use Boolean
  const isCannabisAdvContent = (getProp(config, "meta.mainSection", null) === "/cannabis/adv");
  const isDisallowedPage = disallowed.indexOf(getProp(config, "meta.pagetype")) >= 0;
  const isInAppView = getProp(config, "env.inAppView", false);
  const isInternalTools = /^https:\/\/([\w+\d+\-\.])+\.tgam\.arcpublishing\.com\/(pb|ellipsis|composer|pf)?/im.test(url);
  const isPbAdmin = getProp(config, "env.isPbAdmin", false);
  const epochNow = Math.floor(new Date().getTime() / 1000);
  const timedDisabled = isTimedDisabled(config, epochNow, "env.keytarDisableStart", "env.keytarDisableEnd");

  const paywallStatus = getProp(config, "datalayer.article.paywallStatus", "").toLowerCase();
  const isGreenStatus = paywallStatus.indexOf("green") >= 0;

  const rules = {
    isDisallowedPage, // Don't run on these page types
    isAccessibleAdvContent: isAdvContent && !isCannabisAdvContent, // Don't run on advertorial content, unless it's Cannabis
    isInAppView, // Don't run when inAppView
    isGreenStatus: isGreenStatus && !isCannabisAdvContent, // Don't run on green content, unless it's Cannabis
    isInternalTools, // Don't run on PageBuilder Tools
    isPbAdmin, // Don't run on PageBuilder Admin (localhost)
    timedDisabled // Don't run if keytar is time disabled
  };

  const shouldRun = Object.values(rules).every(item => item === false);
  logger.info(`${debugTag} run:`, shouldRun, rules);
  return shouldRun;
}

/**
 * Adds analytics for a Hermes customer
 * @param {Object} wall - or metered wall class
 * @param {Object} [reason={}] - Hermes' reason for showing the wall
 * @param {String|null} algorithmName - the name of the algorithm that Hermes used
 */
function pushHermesAnalytics(wall, reason = {}, algorithmName) {
  const version = algorithmName.slice(-1); //Gets the Hermes version from the algorithm name
  let hermesEvent = {
    "event": "simpleEvent",
    "simpleEvent": {
      "action": "hermes-scope",
      "label": version
    }
  };
  if (algorithmName) {
    hermesEvent.simpleEvent.algorithmName = algorithmName;
  }
  window.analyticsLayer.push(hermesEvent);

  if (!wall) {
    // analytics for when Hermes returns "nowall"
    window.analyticsLayer.push({
      event: "wallEvent",
      wallEvent: {
        "type": "no-wall",
        "name": undefined,
        "meterCount": undefined,
        "meterLimit": undefined,
        "isDynamic": true,
        "adsVisible": true,
        "countVisibleParagraphs": undefined,
        "wallReason": reason
      }
    });
  }
}

/**
 * Process the active wall and generate the done event
 * @param {Object|null} wall - or metered wall class
 * @param {{ takeover: Boolean, reason: Object }} hermes - Hermes data
 * @param {String} contentId - for the viewed article
 * @param {String} paywallStatus - for the viewed article
 * @returns {Promise<{ accessAllowed: Boolean, blockoutType: String }>} of done event data
 */
function runKeytarWall(wall, hermes, contentId, paywallStatus) {
  // use defaults values, if no wall instance
  const blockoutType = getProp(wall, "template.props.blockoutType", BLOCKOUT_TYPES.NOTHING);

  if (!wall || !wall.enabled) {
    return Promise.resolve({ accessAllowed: true, blockoutType });
  }
  window.keytar = wall;
  const hermesAnalytics = hermes.takeover ? { isDynamic: true, wallReason: hermes.reason } : {};

  return wall.pageview(hermes.takeover, hermesAnalytics, contentId, paywallStatus)
    .then(accessAllowed => {
      return { accessAllowed, blockoutType };
    });
}

/**
 * When an active Keytar Wall instance is found `window.keytar` is added as a prop
 * of the `window` object for debugging and inspecting purposes.
 * It does not override the window.tgam.keytar object, which is a
 * distinct object and should only contain readable system properties
 * @param {Object} profile - user data profile
 * @param {Object} [bypass={}] - bypass data
 * @param {Boolean} bypass.shouldBypass - true if keytar should not run
 * @param {Object} [hermes={}] - Hermes data
 * @param {String} paywallStatus - of article
 * @param {String} contentId - of article
 * @returns {Promise} of nothing
 */
export default function init(profile, bypass = {}, hermes = {}, paywallStatus, contentId) {
  logger.groupCollapsed(`${debugTag} init`);

  // Do not run keytar if user management API fails
  if (!isEmpty(bypass) && bypass.shouldBypass) {
    logger.info(`${debugTag} init bypass keytar`);
    logger.groupEnd(`${debugTag} init`);
    const done = { accessAllowed: true, ...bypass }; // allow access to content
    keytarDone.resolve(done);
    return;
  }
  mark("keytar_started");

  // Hermes takes over when it has a wall type and the user is in scope
  let wallType = getProp(hermes, "wall");
  const hermesAlgorithm = getProp(hermes, "algorithmName");
  // Hermes 3 returns "paywall" instead of "redwall"
  // This updates 'wallType` to ensure the subscriber wall is shown
  if (hermesAlgorithm === "Hermes3" && wallType === "paywall") {
    wallType = "redwall";
  }

  hermes.takeover = wallType !== null;
  let wall = null;

  if (hermes.takeover) {
    const walls = [flashSalePaywallHard, subscriberWall, anonymousWall]; // walls that Hermes can use
    wall = getHermesWall({ walls, wallType, profile });
    pushHermesAnalytics(wall, hermes.reason, hermes.algorithmName);

  } else {
    // Order walls from most to least specific
    const walls = [
      newsletterStoryRegwall, // for access to articles via utm_source parameter
      newslettersWall, // for the Newsletters page
      crosswordsWall, // for puzzle and crosswords pages
      housePriceWall, // for a single special report article (subscribed users).
      globeAdvisorWall, // for blue articles under globe-advisor urls (GA entitlement).
      cannabisSponsoredWall, // for gated or sponsored-content cannabis articles.
      promotionWall, // for red, yellow, blue articles (anonymous, registered, subscribed).
      flashSalePaywallHard, // for red articles (subscribed users).
      subscriberWall, // for red articles (subscribed users).
      anonymousWall, // for yellow and blue articles (anonymous users).
      // flashSalePaywallMetered, // for yellow and blue articles (registered users). Metered should no longer be a thing since Hermes runs 100% of the time now.
      registeredWall // for yellow and blue articles (registered users).
    ];

    wall = shouldRunKeytar() ? getKeytarWall({ walls, profile, paywallStatus }) : null;
  }

  logger.info(`${debugTag} wall init:`, wall);
  logger.groupEnd(`${debugTag} init`);

  if (!window.tgam.keytar && contentShouldAlwaysBeProtected(paywallStatus)) {
    const e = new CustomEvent("contentIntegrityCompromised");
    window.dispatchEvent(e);
    return keytarDone.resolve({
      accessAllowed: false,
      blockoutType: BLOCKOUT_TYPES.NOTHING
    });
  }

  return runKeytarWall(wall, hermes, contentId, paywallStatus)
    .then(done => {
      logger.info(`${debugTag} done event:`, done);
      mark("keytar_done");
      measure("keytar");
      keytarDone.resolve(done);
      return done;
    });
}
